// ═══════════════════════════════════════════════════════════════
// DONOR RANKING REPORT
// Path: src/components/reports/DonorRankingReport.jsx
//
// Two modes:
//   1. Top N donors by total giving (default: Top 100)
//   2. All donors above a giving threshold (default: $1,000)
//
// Date range filter with quick-range buttons. Exportable to CSV.
// Self-contained — fetches its own data.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Trophy, DollarSign, Download, Filter, Users } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";
import { getFiscalDates } from "../../utils/fiscalYear";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount || 0);

export const DonorRankingReport = () => {
  const { palette, chapterSettings, navigate, dataVersion } = useApp();
  const fiscalStart = chapterSettings?.fiscalYearStartMonth || 1;
  const fiscal = getFiscalDates(fiscalStart);

  // Date range
  const [dateFrom, setDateFrom] = useState(fiscal.yearStart);
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  // Mode
  const [mode, setMode] = useState("top"); // "top" or "threshold"
  const [topN, setTopN] = useState(100);
  const [threshold, setThreshold] = useState(1000);

  // Data
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick ranges
  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };

  const quickRanges = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastMonthStart = month === 1
      ? `${year - 1}-12-01`
      : `${year}-${String(month - 1).padStart(2, "0")}-01`;
    const lastMonthEnd = new Date(year, month - 1, 0);
    const lastMonthEndStr = lastMonthEnd.toISOString().slice(0, 10);

    // Prior fiscal year
    const prevFyStartYear = parseInt(fiscal.yearStart.slice(0, 4)) - 1;
    const prevFyStart = `${prevFyStartYear}-${fiscal.yearStart.slice(5)}`;
    const prevFyEnd = new Date(new Date(fiscal.yearStart + "T12:00:00").getTime() - 86400000);
    const prevFyEndStr = prevFyEnd.toISOString().slice(0, 10);
    const prevFyLabel = fiscalStart === 1
      ? `CY ${prevFyStartYear}`
      : `FY ${prevFyStartYear}/${String(prevFyStartYear + 1).slice(2)}`;

    return [
      { label: "This Month", from: monthStr, to: now.toISOString().slice(0, 10) },
      { label: "Last Month", from: lastMonthStart, to: lastMonthEndStr },
      { label: fiscal.label, from: fiscal.yearStart, to: now.toISOString().slice(0, 10) },
      { label: prevFyLabel, from: prevFyStart, to: prevFyEndStr },
      { label: `CY ${year}`, from: `${year}-01-01`, to: now.toISOString().slice(0, 10) },
      { label: `CY ${year - 1}`, from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
      { label: "All Time", from: "2000-01-01", to: now.toISOString().slice(0, 10) },
    ];
  }, [fiscal]);

  // Fetch gifts with donor info (cursor-paginated by id)
  const loadGifts = useCallback(async () => {
    setLoading(true);
    try {
      let allGifts = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, amount, gift_date, fund_id, member_id, funds (name), members:member_id (id, first_name, last_name, email, organization, pathway)")
          .gte("gift_date", dateFrom)
          .lte("gift_date", dateTo)
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allGifts = allGifts.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }
      setGifts(allGifts);
    } catch (err) {
      console.error("DonorRankingReport load error:", err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, dataVersion]);

  useEffect(() => { loadGifts(); }, [loadGifts]);

  // Aggregate by donor
  const donorRankings = useMemo(() => {
    const map = {};
    gifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      if (!map[mid]) {
        const m = g.members || {};
        map[mid] = {
          id: mid,
          name: `${m.last_name || ""}, ${m.first_name || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, ""),
          firstName: m.first_name || "",
          lastName: m.last_name || "",
          email: m.email || "",
          organization: m.organization || "",
          pathway: m.pathway || "",
          director: m.assigned_director || m.assigned_manager || "",
          giftCount: 0,
          totalGiven: 0,
          largestGift: 0,
          firstGift: g.gift_date,
          lastGift: g.gift_date,
          funds: new Set(),
        };
      }
      const amount = parseFloat(g.amount) || 0;
      map[mid].giftCount++;
      map[mid].totalGiven += amount;
      if (amount > map[mid].largestGift) map[mid].largestGift = amount;
      if (g.gift_date < map[mid].firstGift) map[mid].firstGift = g.gift_date;
      if (g.gift_date > map[mid].lastGift) map[mid].lastGift = g.gift_date;
      if (g.funds?.name) map[mid].funds.add(g.funds.name);
    });

    let ranked = Object.values(map).sort((a, b) => b.totalGiven - a.totalGiven);

    // Apply mode filter
    if (mode === "top") {
      ranked = ranked.slice(0, topN);
    } else {
      ranked = ranked.filter((d) => d.totalGiven >= threshold);
    }

    // Add rank
    ranked.forEach((d, i) => {
      d.rank = i + 1;
      d.fundList = Array.from(d.funds).join(", ");
    });

    return ranked;
  }, [gifts, mode, topN, threshold]);

  // Summary stats
  const stats = useMemo(() => {
    const total = donorRankings.reduce((s, d) => s + d.totalGiven, 0);
    const count = donorRankings.length;
    const avgGift = count > 0 ? total / donorRankings.reduce((s, d) => s + d.giftCount, 0) : 0;
    const totalGifts = donorRankings.reduce((s, d) => s + d.giftCount, 0);
    return { total, count, avgGift, totalGifts };
  }, [donorRankings]);

  // Export CSV
  const handleExport = () => {
    if (donorRankings.length === 0) return;
    const headers = ["Rank", "Last Name", "First Name", "Organization", "Email", "Director", "Gifts", "Total Given", "Largest Gift", "First Gift", "Last Gift", "Funds"];
    const rows = donorRankings.map((d) => [
      d.rank,
      `"${d.lastName}"`,
      `"${d.firstName}"`,
      `"${d.organization}"`,
      `"${d.email}"`,
      `"${d.director}"`,
      d.giftCount,
      d.totalGiven.toFixed(2),
      d.largestGift.toFixed(2),
      d.firstGift,
      d.lastGift,
      `"${d.fundList}"`,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `donor-ranking-${mode === "top" ? `top${topN}` : `over${threshold}`}-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Styles
  const th = {
    padding: "6px 10px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
    position: "sticky",
    top: 0,
    background: palette.cardBg,
  };

  const td = {
    padding: "5px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  return (
    <div>
      {/* ── Controls ─────────────────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text2, fontFamily: FONT_FAMILY }}>
            Date Range:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <div style={{ width: 1, height: 20, background: palette.cardBorder, margin: "0 4px" }} />
          {quickRanges.map((qr) => (
            <button
              key={qr.label}
              onClick={() => setRange(qr.from, qr.to)}
              style={{
                padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
                fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
                background: dateFrom === qr.from && dateTo === qr.to ? palette.accent1 : `${palette.accent1}10`,
                color: dateFrom === qr.from && dateTo === qr.to ? "#fff" : palette.accent1,
                transition: "all 0.15s",
              }}
            >
              {qr.label}
            </button>
          ))}
        </div>

        {/* Mode selector */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Trophy size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text2, fontFamily: FONT_FAMILY }}>
            Show:
          </span>
          <button
            onClick={() => setMode("top")}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
              fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
              background: mode === "top" ? palette.accent1 : `${palette.accent1}10`,
              color: mode === "top" ? "#fff" : palette.accent1,
            }}
          >
            Top Donors
          </button>
          <button
            onClick={() => setMode("threshold")}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
              fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
              background: mode === "threshold" ? palette.accent1 : `${palette.accent1}10`,
              color: mode === "threshold" ? "#fff" : palette.accent1,
            }}
          >
            Above Threshold
          </button>
          <div style={{ width: 1, height: 20, background: palette.cardBorder, margin: "0 4px" }} />
          {mode === "top" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>Top</span>
              <select
                value={topN}
                onChange={(e) => setTopN(parseInt(e.target.value))}
                style={{
                  padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
                  background: palette.inputBg, color: palette.inputColor, cursor: "pointer",
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
              <span style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>donors</span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>Minimum $</span>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                style={{
                  padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
                  background: palette.inputBg, color: palette.inputColor, width: 90,
                }}
              />
            </div>
          )}
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
          Loading donor data...
        </div>
      ) : (
        <>
          {/* ── Summary ────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <Users size={ICON_SIZES.sm} style={{ color: palette.accent1, marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.count.toLocaleString()}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                {mode === "top" ? `Top ${topN} Donors` : `Donors ≥ ${fmtCurrency(threshold)}`}
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: "#16a34a", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(stats.total)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Combined Giving
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <Trophy size={ICON_SIZES.sm} style={{ color: "#f59e0b", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.totalGifts.toLocaleString()}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Total Gifts
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: "#8b5cf6", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(stats.avgGift)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Avg Gift Size
              </div>
            </Card>
          </div>

          {/* ── Ranking Table ──────────────────────────────────── */}
          <Card>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12,
            }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
              }}>
                <Trophy size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
                {mode === "top" ? `Top ${topN} Donors` : `Donors Giving $${threshold.toLocaleString()}+`}
              </div>
              <ActionButton label="Export CSV" icon={Download} small variant="secondary" onClick={handleExport} />
            </div>

            {donorRankings.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                No donors match the current criteria.
              </div>
            ) : (
              <div style={{ maxHeight: 600, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: "center", width: 40 }}>#</th>
                      <th style={th}>Donor</th>
                      <th style={th}>Organization</th>
                      <th style={th}>Director</th>
                      <th style={{ ...th, textAlign: "right" }}>Gifts</th>
                      <th style={{ ...th, textAlign: "right" }}>Total Given</th>
                      <th style={{ ...th, textAlign: "right" }}>Largest</th>
                      <th style={th}>Last Gift</th>
                      <th style={th}>Funds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donorRankings.map((d) => (
                      <tr
                        key={d.id}
                        style={{ cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = `${palette.accent1}06`}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        onClick={() => navigate("memberDetail", { id: d.id })}
                      >
                        <td style={{
                          ...td, textAlign: "center", fontWeight: 700,
                          color: d.rank <= 3 ? "#f59e0b" : d.rank <= 10 ? palette.accent1 : palette.text3,
                        }}>
                          {d.rank}
                        </td>
                        <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>
                          {d.name}
                        </td>
                        <td style={{ ...td, color: palette.text2 }}>{d.organization || "—"}</td>
                        <td style={{ ...td, color: palette.text2 }}>{d.director || "—"}</td>
                        <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{d.giftCount}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700, color: palette.accent1 }}>
                          {fmtCurrency(d.totalGiven)}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: palette.text2 }}>
                          {fmtCurrency(d.largestGift)}
                        </td>
                        <td style={{ ...td, color: palette.text2 }}>{d.lastGift}</td>
                        <td style={{ ...td, color: palette.text2, fontSize: "0.65rem", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {d.fundList || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
